// A box with rounded edges and corners, as plain arrays (no three.js, so roundedBox.test.ts can check it); RoomModel
// turns it into faces with the light baked in from the smooth normals. Used for the furniture on High (chairs, bench).
//
// How it is built: each of the six faces is a grid. A grid point p on the box's surface is pulled onto the rounded
// surface by clamping it into the inner box (the box shrunk by the radius) and going out from there by the radius:
// c = clamp(p), position = c + radius * normalize(p - c), normal = normalize(p - c). The flat middle of a face maps to
// itself; near the edges the grid lines are placed at equal angles (radius * tan) so each quarter round gets
// `segments` equal steps, half on each of the two faces that meet there. Neighbouring faces share their seam points
// exactly (same position, same normal), so the shading is smooth across the edges and corners.
//
// A face can also puff out (`bulge`, a cushion's top), smoothly to nothing where the rounding starts, and a caller can
// add its own small relief on the flat part (`relief`, e.g. the buttons of a tufted cushion).

export type V3 = [number, number, number];
export type FaceName = "+x" | "-x" | "+y" | "-y" | "+z" | "-z";

export interface RoundedBoxOptions {
    /** edge radius; limited to half the smallest side */
    radius: number;
    /** steps per quarter round (rounded up to even); 0 = a plain box with sharp edges */
    segments: number;
    /** largest spacing of the grid on the flat part of the faces (world units); default: no extra subdivision */
    spacing?: number;
    /** put grid lines of the flat part on multiples of this (world units, e.g. a button pattern), as well as `spacing` */
    snap?: number;
    /** the faces `snap` applies to (default all) */
    snapFaces?: FaceName[];
    /** how far the middle of a face puffs out */
    bulge?: Partial<Record<FaceName, number>>;
    /** faces left out (against the floor or a wall) */
    skip?: FaceName[];
    /** extra height along the face normal at a point of a face's flat part, and its gradient (world units) */
    relief?: (face: FaceName, p: V3) => {height: number; gradient: V3} | null;
}

export interface RoundedBoxMesh {
    positions: V3[];
    normals: V3[];
    /** world position along the face's u and v directions (the same as RoomBuilder.box's tiled UVs) */
    uvs: [number, number][];
    /** true for points on a seam between two faces (where a cushion's piping runs) */
    seam: boolean[];
    faceOf: FaceName[];
    triangles: [number, number, number][];
}

// u and v of each face as in RoomBuilder.box: axis index and sign; the normal is u x v
const FACES: {name: FaceName; axis: number; sign: number; u: [number, number]; v: [number, number]}[] = [
    {name: "+x", axis: 0, sign: 1, u: [2, -1], v: [1, 1]},
    {name: "-x", axis: 0, sign: -1, u: [2, 1], v: [1, 1]},
    {name: "+z", axis: 2, sign: 1, u: [0, 1], v: [1, 1]},
    {name: "-z", axis: 2, sign: -1, u: [0, -1], v: [1, 1]},
    {name: "+y", axis: 1, sign: 1, u: [0, 1], v: [2, -1]},
    {name: "-y", axis: 1, sign: -1, u: [0, 1], v: [2, 1]},
];

const norm = (a: V3): V3 => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };

/** Grid coordinates along one axis of a face: the rounding near lo, the flat part, the rounding near hi. */
export function axisCoordinates(lo: number, hi: number, radius: number, half: number, spacing: number, snap: number): number[] {
    const cLo = lo + radius, cHi = hi - radius, out: number[] = [];
    for (let k = half; k >= 1; k--) out.push(cLo - radius * Math.tan(Math.PI / 4 * k / half));
    out.push(cLo);
    if (cHi - cLo > 1e-9) {
        const inner: number[] = [];
        if (snap > 0) for (let t = Math.ceil(cLo / snap + 1e-6) * snap; t < cHi - 1e-6; t += snap) inner.push(t);
        const edges = [cLo, ...inner, cHi], fill: number[] = [];
        for (let i = 0; i < edges.length - 1; i++) {
            const n = spacing > 0 ? Math.max(1, Math.ceil((edges[i + 1] - edges[i]) / spacing - 1e-9)) : 1;
            for (let j = 1; j <= n; j++) fill.push(edges[i] + (edges[i + 1] - edges[i]) * j / n);
        }
        out.push(...fill);
    }
    for (let k = 1; k <= half; k++) out.push(cHi + radius * Math.tan(Math.PI / 4 * k / half));
    return out;
}

// the bulge profile: 1 in the middle, 0 with zero slope at the edges of the flat part (no crease where the rounding starts)
const puff = (u: number) => { const s = 1 - u * u; return s * s; };
const puffSlope = (u: number) => -4 * u * (1 - u * u);

export function roundedBox(min: V3, max: V3, o: RoundedBoxOptions): RoundedBoxMesh {
    const size: V3 = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
    const half = o.segments > 0 ? Math.ceil(o.segments / 2) : 0;
    const r = half > 0 ? Math.max(0, Math.min(o.radius, size[0] / 2, size[1] / 2, size[2] / 2)) : 0;
    const cLo: V3 = [min[0] + r, min[1] + r, min[2] + r], cHi: V3 = [max[0] - r, max[1] - r, max[2] - r];
    const mesh: RoundedBoxMesh = {positions: [], normals: [], uvs: [], seam: [], faceOf: [], triangles: []};
    const spacing = o.spacing || 0, snap = o.snap || 0;
    for (const f of FACES) {
        if (o.skip && o.skip.includes(f.name)) continue;
        const [ua, us] = f.u, [va, vs] = f.v;
        const fs = !o.snapFaces || o.snapFaces.includes(f.name) ? snap : 0;
        const A = axisCoordinates(min[ua], max[ua], r, half, spacing, fs), B = axisCoordinates(min[va], max[va], r, half, spacing, fs);
        const bulge = (o.bulge && o.bulge[f.name]) || 0;
        const n0: V3 = [0, 0, 0]; n0[f.axis] = f.sign;
        const base = mesh.positions.length;
        for (let j = 0; j < B.length; j++) {
            for (let i = 0; i < A.length; i++) {
                const p: V3 = [0, 0, 0];
                p[f.axis] = f.sign > 0 ? max[f.axis] : min[f.axis];
                p[ua] = A[i]; p[va] = B[j];
                const c: V3 = [0, 1, 2].map(k => Math.min(cHi[k], Math.max(cLo[k], p[k]))) as V3;
                let normal: V3 = r > 0 ? norm([p[0] - c[0], p[1] - c[1], p[2] - c[2]]) : n0;
                let pos: V3 = r > 0 ? [c[0] + normal[0] * r, c[1] + normal[1] * r, c[2] + normal[2] * r] : p;
                // the flat part: puff and relief along the face normal, the normal tilted by their gradient
                const inside = A[i] >= cLo[ua] - 1e-9 && A[i] <= cHi[ua] + 1e-9 && B[j] >= cLo[va] - 1e-9 && B[j] <= cHi[va] + 1e-9;
                if (inside) {
                    let h = 0;
                    const g: V3 = [0, 0, 0];
                    const hu = (cHi[ua] - cLo[ua]) / 2, hv = (cHi[va] - cLo[va]) / 2;
                    if (bulge && hu > 1e-9 && hv > 1e-9) {
                        const u = (A[i] - (cLo[ua] + hu)) / hu, v = (B[j] - (cLo[va] + hv)) / hv;
                        h += bulge * puff(u) * puff(v);
                        g[ua] += bulge * puffSlope(u) * puff(v) / hu;
                        g[va] += bulge * puff(u) * puffSlope(v) / hv;
                    }
                    const rel = o.relief ? o.relief(f.name, p) : null;
                    if (rel) {
                        h += rel.height;
                        for (const k of [ua, va]) g[k] += rel.gradient[k];
                    }
                    if (h !== 0 || g[ua] !== 0 || g[va] !== 0) {
                        pos = [pos[0] + n0[0] * h, pos[1] + n0[1] * h, pos[2] + n0[2] * h];
                        normal = norm([n0[0] - g[0], n0[1] - g[1], n0[2] - g[2]]);
                    }
                }
                mesh.positions.push(pos);
                mesh.normals.push(normal);
                mesh.uvs.push([us * pos[ua], vs * pos[va]]);
                mesh.seam.push(half > 0 && (i === 0 || i === A.length - 1 || j === 0 || j === B.length - 1));
                mesh.faceOf.push(f.name);
            }
        }
        // i and j run along the positive world axes; wind the triangles anticlockwise seen from outside
        const e = (k: number): V3 => { const t: V3 = [0, 0, 0]; t[k] = 1; return t; };
        const x = ((a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]])(e(ua), e(va));
        const flip = x[0] * n0[0] + x[1] * n0[1] + x[2] * n0[2] < 0;
        const row = A.length;
        for (let j = 0; j < B.length - 1; j++) {
            for (let i = 0; i < A.length - 1; i++) {
                const a = base + j * row + i, b = a + 1, c = a + row + 1, d = a + row;
                if (flip) mesh.triangles.push([a, c, b], [a, d, c]);
                else mesh.triangles.push([a, b, c], [a, c, d]);
            }
        }
    }
    return mesh;
}
